import React, { useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";
const TWO_FA_TOKEN_KEY = "ct_2fa_token";
const TWO_FA_USER_KEY = "ct_2fa_user";
const EMAIL_OTP_TOKEN_KEY = "ct_email_otp_token";

function defaultPathForRole(role) {
  return role === "nexo" ? "/nexo" : role === "secret" ? "/x-portal" : "/";
}

export default function TwoFactorVerify() {
  const navigate = useNavigate();
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const verifyingRef = useRef(false);

  const tempToken = localStorage.getItem(TWO_FA_TOKEN_KEY);

  const submitOtp = async (e, code = totpCode) => {
    e?.preventDefault();
    if (verifyingRef.current) return;

    if (!tempToken) {
      navigate("/login", { replace: true });
      return;
    }

    verifyingRef.current = true;
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/2fa/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tempToken}`,
        },
        body: JSON.stringify({ totpCode: code }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Invalid authenticator code");
      }

      if (data.requiresEmailOtp) {
        localStorage.setItem(EMAIL_OTP_TOKEN_KEY, data.emailOtpToken);
        localStorage.setItem(TWO_FA_USER_KEY, JSON.stringify(data.user));
        navigate("/email-otp", { replace: true });
        return;
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.removeItem(TWO_FA_TOKEN_KEY);
      localStorage.removeItem(TWO_FA_USER_KEY);

      if (["admin", "secret"].includes(data.user?.role)) {
        localStorage.setItem("ct_admin_auth", "true");
      } else {
        localStorage.removeItem("ct_admin_auth");
      }

      navigate(defaultPathForRole(data.user?.role), { replace: true });
    } catch (err) {
      setError(err.message || "Invalid authenticator code");
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111827] text-white flex items-center justify-center px-4">
      <form onSubmit={submitOtp} className="w-full max-w-[440px] bg-[#1f2937] rounded-xl px-8 py-8 shadow-2xl">
        <div className="flex flex-col items-center mb-7">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center mb-4">
            <ShieldCheck size={30} />
          </div>
          <h1 className="text-2xl font-bold">Two Factor Login</h1>
          <p className="text-gray-300 text-sm mt-2 text-center">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>

        <input
          className="w-full bg-[#374151] border border-[#4b5563] rounded-lg py-3 px-3 mb-4 text-center tracking-[0.35em] text-xl"
          placeholder="000000"
          value={totpCode}
          maxLength={6}
          disabled={loading}
          onChange={(e) => {
            const code = e.target.value.replace(/\D/g, "").slice(0, 6);
            setTotpCode(code);
            if (code.length === 6 && code !== totpCode) submitOtp(undefined, code);
          }}
        />

        {error && <div className="text-red-300 text-sm mb-4">{error}</div>}

        <button disabled={loading} className="w-full bg-blue-500 hover:bg-blue-400 rounded py-3 font-semibold disabled:opacity-60">
          {loading ? "Verifying..." : "Verify Login"}
        </button>
      </form>
    </div>
  );
}
